import { Link } from "react-router-dom";
import {
  PRICING,
  PRIVACY_POLICY,
  TERMS_OF_SERVICE,
  SITEMAP,
} from "@/lib/links";
import {
  FacebookLogo,
  LinkedinLogo,
  EnvelopeSimple,
  RedditLogo,
  Butterfly,
  XLogo,
} from "@phosphor-icons/react";
import { useA11ySettings } from "@/lib/hooks/useA11ySettings";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const { t } = useTranslation("common");
  const { darkMode } = useA11ySettings();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="pt-24 border-t bg-card border-border">
      <div className="w-full px-10 py-12 md:px-20">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <img
                src={
                  darkMode
                    ? "/assets/TT logo white.png"
                    : "/assets/TT logo black.png"
                }
                alt="Teacher Tools"
                className="h-8"
              />
              <span className="text-lg font-semibold sr-only text-foreground">
                Teacher Tools
              </span>
            </div>
            <p className="max-w-xs text-sm text-muted-foreground">
              {t("footer.description")}
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">
              {t("footer.community.title")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="https://www.facebook.com/groups/722840903696499"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 transition-colors text-muted-foreground hover:text-primary"
                >
                  <FacebookLogo size={16} />
                  {t("footer.community.facebook")}
                </a>
              </li>
              <li>
                <a
                  href="https://www.reddit.com/r/TeacherTools/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 transition-colors text-muted-foreground hover:text-primary"
                >
                  <RedditLogo size={16} />
                  {t("footer.community.reddit")}
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/groups/13323124/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 transition-colors text-muted-foreground hover:text-primary"
                >
                  <LinkedinLogo size={16} />
                  {t("footer.community.linkedin")}
                </a>
              </li>
              <li>
                <a
                  href="https://x.com/tchrtools"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 transition-colors text-muted-foreground hover:text-primary"
                >
                  <XLogo size={16} />
                  {t("footer.community.twitter")}
                </a>
              </li>
              <li>
                <a
                  href="https://bsky.app/profile/tchr.tools"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 transition-colors text-muted-foreground hover:text-primary"
                >
                  <Butterfly size={16} />
                  {t("footer.community.bluesky")}
                </a>
              </li>
              <li>
                <a
                  href="mailto:hello@tchr.tools"
                  className="flex items-center gap-2 transition-colors text-muted-foreground hover:text-primary"
                >
                  <EnvelopeSimple size={16} />
                  {t("footer.community.email")}
                </a>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">
              {t("footer.company.title")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={PRICING}
                  className="transition-colors text-muted-foreground hover:text-primary"
                >
                  {t("footer.company.pricing")}
                </Link>
              </li>
              {/* <li>
                <a
                  href="/docs"
                  className="transition-colors text-muted-foreground hover:text-primary"
                >
                  Documentation
                </a>
              </li>
              <li>
                <a
                  href="/support"
                  className="transition-colors text-muted-foreground hover:text-primary"
                >
                  Support
                </a>
              </li> */}
              <li>
                <Link
                  to="https://whiteboard.tchr.tools"
                  target="_blank"
                  className="transition-colors text-muted-foreground hover:text-primary"
                >
                  {t("footer.company.blog")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">
              {t("footer.legal.title")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={PRIVACY_POLICY}
                  className="transition-colors text-muted-foreground hover:text-primary"
                >
                  {t("footer.legal.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={TERMS_OF_SERVICE}
                  className="transition-colors text-muted-foreground hover:text-primary"
                >
                  {t("footer.legal.terms")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between w-full gap-4 pt-8 mt-8 border-t border-border md:flex-row">
          <p className="text-sm text-muted-foreground">
            {t("footer.copyright", { year: currentYear })}
          </p>
          <div className="flex gap-6 text-sm md:pr-48">
            {/* <a
              href="/accessibility"
              className="transition-colors text-muted-foreground hover:text-primary"
            >
              Accessibility
            </a> */}
            <a
              href={SITEMAP}
              className="transition-colors text-muted-foreground hover:text-primary"
            >
              {t("footer.sitemap")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
